import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { agentIdentifierLogin } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Loader2, UserCog, Eye, EyeOff } from "lucide-react";

export default function AgentLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !password) { toast.error("Enter mobile/email and password"); return; }
    setLoading(true);
    try {
      const res = await agentIdentifierLogin(identifier.trim(), password);
      login(res.data.token, res.data.user, "agent");
      toast.success("Login successful!");
      navigate("/agent/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail || "Invalid credentials";
      toast.error(detail);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#EEF2FF] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-[#1565C0] to-[#0D47A1] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <UserCog className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-black text-[#0A2540]" style={{fontFamily:'Cabinet Grotesk, Outfit, sans-serif'}}>Agent Login</h1>
          <p className="text-sm text-gray-500 mt-1">Sign in with your mobile number or email</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8">
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-2 block">Mobile Number or Email</Label>
              <Input
                data-testid="agent-login-identifier"
                value={identifier}
                onChange={e => setIdentifier(e.target.value)}
                placeholder="10-digit mobile or email@example.com"
                className="rounded-xl"
                autoComplete="username"
              />
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-2 block">Password</Label>
              <div className="relative">
                <Input
                  data-testid="agent-login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="rounded-xl pr-10"
                  autoComplete="current-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button data-testid="agent-login-submit" type="submit" disabled={loading} className="w-full bg-[#1565C0] hover:bg-[#0D47A1] text-white rounded-full py-3 font-semibold mt-2">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Login"}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-gray-100 text-center space-y-2">
            <button onClick={() => navigate('/agent/forgot-password')} className="text-sm text-[#1565C0] font-semibold hover:underline">
              Forgot Password?
            </button>
            <p className="text-sm text-gray-500">
              Don't have an account? <button onClick={() => navigate('/agent-register')} className="text-[#1565C0] font-semibold hover:underline">Register as Agent</button>
            </p>
            <button onClick={() => navigate('/')} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 mx-auto">
              <ArrowLeft className="h-3 w-3" /> Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
